import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { useApi } from "~hooks/apiHooks/useApi";
import { useInvalidateCache } from "~hooks/useInvalidateCache.ts";
import { useUpdatePermissionScopeCache } from "~hooks/useUpdatePermissionScopeCache.ts";
import type { Season, SeasonResponse } from "~types/season.ts";

export const useUpdateSeason = () => {
  const { sport = "", organizationId = "", seasonId = "" } = useParams();
  const queryClient = useQueryClient();
  const request = useApi();
  const { mutateAsync: invalidateCache } = useInvalidateCache();
  const updatePermissionScopeCache = useUpdatePermissionScopeCache();

  return useMutation<Season | undefined, Error, Partial<Season>>({
    mutationFn: async (body) => {
      const response = await request<SeasonResponse>(`${sport}/o/${organizationId}/seasons/${seasonId}`, {
        method: "PUT",
        body,
      });

      return response?.data?.[0];
    },
    onSuccess: async (updatedSeason) => {
      if (!updatedSeason?.seasonId) {
        return;
      }

      updatePermissionScopeCache(sport, organizationId, (updatedScope) => {
        const existingSeason = updatedScope.resources?.seasons?.[seasonId];

        if (existingSeason && updatedScope.resources.seasons) {
          updatedScope.resources.seasons[seasonId] = {
            ...existingSeason,
            ...updatedSeason,
            seasonId,
          };
        }
      });

      await Promise.all([invalidateCache(), queryClient.invalidateQueries({ queryKey: ["seasons", sport, organizationId, seasonId] })]);
    },
  });
};
